/* eslint-disable jsx-a11y/tabindex-no-positive */
import { useState } from 'react';

import { Accordion, Button, Icon } from '@iwoca/orion';
import { FormattedMessage, injectIntl } from 'react-intl';
import { reduxForm, getValues } from 'redux-form';

import fields from './fields';
import styles from './FullLoan.module.css';
import messages from './LoanRequestDetails.messages';
import { loanRequestDetailsSchema } from './schema';
import schemaValidator from './validate';
import FormField from '../../components/FormField';
import { currencySymbols, localeCurrency } from '../../helpers/constants';
import {
    intlAmountFormatterDecorator,
    numbersOnlyParser
} from '../../helpers/helpers';
import normalizer from '../../helpers/inputNormalizer';
import { getCountryCode } from '../../lendingAPI/lendingAPISelector';
import {
    getRequestedAmount,
    getSplitTestValue,
    isDeRBLLoan
} from '../../utils/selectors';
import commonMessages from '../Containers.messages';

const FullLoan = (props) => {
    const [initialAmount] = useState(props.requestedAmount);

    const {
        fields: { loan_request },
        handleSubmit,
        mandatory,
        firstName,
        currency,
        country,
        isDeRBLLoan,
        intl,
        bankStatementsSplitTest,
        requestedAmount
    } = props;

    const submitLoanRequestDetails = () => {
        const { saveStateChanges } = props;
        saveStateChanges();
    };

    const loanAmount = normalizer(
        {
            ...loan_request.amount,
            value: requestedAmount || loan_request.amount.value,
            initialValue: requestedAmount || loan_request.amount.value
        },
        intlAmountFormatterDecorator(intl.formatNumber),
        numbersOnlyParser
    );

    const onClose = () => {
        props.fields.loan_request.amount.onChange(initialAmount);
        props.closeModal();
    };

    const noBankStatementsRequired =
        !bankStatementsSplitTest || bankStatementsSplitTest === 'NOT_REQUIRED';

    const anyBSRequired = [
        'ANY_BS_REQUIRED',
        'NOT_REQUIRED_ANY_IF_DECLINED'
    ].includes(bankStatementsSplitTest);

    const OBRequired = ['OB_REQUIRED', 'NOT_REQUIRED_OB_IF_DECLINED'].includes(
        bankStatementsSplitTest
    );

    return (
        <form
            noValidate
            autoComplete="off"
            className="form-horizontal"
            data-testid="edit-application-modal"
            onSubmit={handleSubmit(submitLoanRequestDetails)}>
            <div className={styles.loanRequestHeaderContainer}>
                <h2 className={styles.loanRequestHeader}>
                    {mandatory ? (
                        <FormattedMessage
                            {...messages.mandatoryTitle}
                            values={{ name: firstName }}
                        />
                    ) : (
                        <FormattedMessage
                            {...commonMessages.editOverlayTitle}
                        />
                    )}
                </h2>

                {!mandatory ? (
                    <Button
                        data-testid="close-edit-loan-request"
                        className={styles.closeIcon}
                        onClick={onClose}
                        variant="tertiary"
                        iconOnly
                        tabIndex={4}>
                        <Icon icon="timesDefault" />
                    </Button>
                ) : null}
            </div>

            <div className={styles.loanRequestFormContainer}>
                <div>
                    {isDeRBLLoan && (
                        <p>
                            Willkommen bei iwoca! Wir müssen Ihnen nur noch ein
                            paar Fragen stellen, bevor wir eine Entscheidung
                            über Ihre Kreditanfrage treffen können. Bitte teilen
                            Sie uns zuerst mit, wie hoch der Betrag ist, den Sie
                            beantragen möchten.
                        </p>
                    )}
                    <FormField
                        field={loanAmount}
                        label={<FormattedMessage {...messages.amount} />}
                        wrapperClassName={styles.formField}
                        wrapperGrid={{}}>
                        <FormField.Input
                            field={loanAmount}
                            addon={true}
                            addonContent={currencySymbols[currency]}
                            addonPosition={
                                currency === 'GBP' ? 'left' : 'right'
                            }
                            pattern="\d*"
                            tabIndex={1}
                        />
                    </FormField>

                    {country === 'de' && (
                        <Accordion
                            small
                            title="Folgende Unterlagen brauchen wir für die jeweiligen Kredithöhen"
                            className={styles.accordion}
                            tabIndex={2}>
                            <h2>1.000 € - 15.000 €</h2>
                            {noBankStatementsRequired && (
                                <ul>
                                    <li>Keine Dokumente benötigt</li>
                                </ul>
                            )}
                            {anyBSRequired && (
                                <ul>
                                    <li>
                                        Kontoumsätze Ihres Geschäftskontos der
                                        letzten 90 Tage
                                    </li>
                                </ul>
                            )}
                            {OBRequired && (
                                <ul>
                                    <li>
                                        Umsätze Ihres Geschäftskontos per Open
                                        Banking übermitteln
                                    </li>
                                </ul>
                            )}

                            <h2>15.001 € - 50.000 €</h2>

                            <ul>
                                <li>
                                    Kontoumsätze Ihres Geschäftskontos der
                                    letzten 90 Tage
                                </li>
                            </ul>

                            <h2>50.001 € - 500.000 €</h2>

                            <ul>
                                <li>
                                    Kontoumsätze Ihres Geschäftskontos der
                                    letzten 90 Tage
                                </li>
                                <li>
                                    BWA und SuSa der letzten beiden
                                    Geschäftsjahre
                                </li>
                                <li>
                                    Aktuelle BWA und SuSa (nicht älter als 4
                                    Monate)
                                </li>
                            </ul>

                            <h2>Tipps</h2>
                            <ul>
                                <li>EÜR ersetzen die BWA</li>
                                <li>Jahresabschlüsse ersetzen BWA und SuSa</li>
                            </ul>
                        </Accordion>
                    )}

                    <Button
                        type="submit"
                        className={styles.button}
                        tabIndex={3}>
                        {mandatory ? (
                            <FormattedMessage {...messages.mandatorySubmit} />
                        ) : (
                            <FormattedMessage
                                {...commonMessages.editOverlaySubmit}
                            />
                        )}
                    </Button>
                </div>
            </div>
        </form>
    );
};

const reduxFormConfig = {
    form: 'sign_up',
    fields,
    asyncBlurFields: fields,
    destroyOnUnmount: false,
    getFormState: (state, reduxMountPoint) => state[reduxMountPoint],
    asyncValidate: schemaValidator(loanRequestDetailsSchema)
};
const mapStateToProps = (state) => {
    const application = state.fetched.application.payload;

    return {
        application,
        formValues: getValues(state.form.sign_up),
        requestedAmount: getRequestedAmount(state),
        ui: state.ui,
        currency: localeCurrency[getCountryCode(state)],
        country: getCountryCode(state),
        isDeRBLLoan: isDeRBLLoan(state),
        bankStatementsSplitTest:
            getSplitTestValue(state, 'de_auto_bs_required_test_2024') ||
            getSplitTestValue(
                state,
                'de_manual_lending_open_banking_required_test_2024_q4'
            )
    };
};

export default injectIntl(
    reduxForm(reduxFormConfig, mapStateToProps)(FullLoan)
);
